import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ShoppingBag, Ticket, Calendar, Coins, Sparkles, X, Loader2 } from "lucide-react";
import AuthTabs from "./AuthTabs";

/**
 * PurchaseAuthGate — v144.3. Sign-in gate for anonymous visitors before
 * they check out a Product (Buy) / Service (Book) / Coupon (Claim). If
 * the creator's loyalty program is enabled, a coins ribbon on top
 * previews how many coins the visitor will earn from this action —
 * mirrors the Follow flow's ribbon pattern.
 *
 * Once auth completes, calls `onAuthed(user)` so the parent can transition
 * to the actual checkout modal with name/email pre-filled from session.
 *
 * Props:
 *   creatorSlug — REQUIRED for the loyalty fetch. Falls back silently
 *                 (no ribbon) if unavailable.
 *   intent      — "buy" | "book" | "claim" (drives copy + icon + coin math)
 *   priceInr    — number, used for `purchase_per_100` math
 *   itemTitle   — optional item name shown in modal header
 *   onClose     — close without auth
 *   onAuthed    — (user) => void, called when session is minted
 */
export default function PurchaseAuthGate({
  creatorSlug,
  intent = "buy",
  priceInr = 0,
  itemTitle,
  onClose,
  onAuthed,
}) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [loyalty, setLoyalty] = useState(null); // {enabled, coin_name, earn_rules}

  useEffect(() => {
    if (!creatorSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${backend}/api/public/creators/${encodeURIComponent(creatorSlug)}/engagement`,
        );
        const d = await r.json();
        if (!cancelled) {
          setLoyalty({
            enabled: !!d.loyalty_enabled,
            coin_name: d.loyalty_coin_name || "coins",
            earn_rules: d.loyalty_earn_rules || {},
          });
        }
      } catch {
        if (!cancelled) setLoyalty({ enabled: false, coin_name: "coins", earn_rules: {} });
      }
    })();
    return () => { cancelled = true; };
  }, [backend, creatorSlug]);

  // Compute coins for this action.
  let coinsEarned = 0;
  if (loyalty?.enabled) {
    if (intent === "claim") {
      coinsEarned = Number(loyalty.earn_rules.coupon_claim || 0);
    } else {
      // buy | book — both go through purchase_per_100
      const per100 = Number(loyalty.earn_rules.purchase_per_100 || 0);
      coinsEarned = Math.floor(Math.max(0, Number(priceInr || 0)) / 100) * per100;
    }
  }
  const hasCoins = coinsEarned > 0;
  const coinName = loyalty?.coin_name || "coins";

  const meta = {
    buy:   { verb: "buy",         icon: <ShoppingBag className="w-4 h-4" strokeWidth={3} />,
             heading: "Sign in to buy",
             sub: itemTitle ? `Checking out: ${itemTitle}` : "Verified checkout — order updates land in your inbox." },
    book:  { verb: "book",        icon: <Calendar    className="w-4 h-4" strokeWidth={3} />,
             heading: "Sign in to book",
             sub: itemTitle ? `Booking: ${itemTitle}` : "Confirm your appointment with a verified account." },
    claim: { verb: "claim",       icon: <Ticket      className="w-4 h-4" strokeWidth={3} />,
             heading: "Sign in to claim",
             sub: itemTitle ? `Claiming: ${itemTitle}` : "Coupons are tied to your account so you can find them later." },
  }[intent] || { verb: "continue", icon: null,
                 heading: "Sign in to continue",
                 sub: "One tap and we save your details for next time." };

  return (
    createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[9997] flex items-end sm:items-center justify-center p-0 sm:p-3"
      onClick={onClose}
      data-testid="purchase-auth-gate-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-page border-2 border-ink shadow-brutal w-full sm:max-w-md overflow-y-auto overscroll-contain flex flex-col"
        style={{
          // v145.18 — Same iOS-Safari viewport fix as HubEngagementBar
          // modal: 100vh doesn't respect the browser toolbar, hiding the
          // submit button. 100dvh + safe-area padding keeps everything
          // reachable.
          maxHeight: "min(100dvh, 100vh)",
          paddingBottom: "env(safe-area-inset-bottom, 0)",
        }}
        data-testid="purchase-auth-gate-modal"
        data-intent={intent}
      >
        {/* Coins ribbon */}
        {hasCoins && (
          <div
            className="bg-sun border-b-2 border-ink px-4 py-2.5 flex items-center gap-2"
            data-testid="purchase-auth-gate-coins-ribbon"
          >
            <div className="w-8 h-8 shrink-0 border-2 border-ink bg-white flex items-center justify-center">
              <Coins className="w-4 h-4" strokeWidth={3} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-black text-sm leading-tight">
                Earn {coinsEarned} {coinName} on this {meta.verb} <Sparkles className="w-3.5 h-3.5 inline align-baseline" strokeWidth={3} />
              </p>
              <p className="text-[10px] font-bold text-ink/70 leading-tight">
                Redeem later for discounts on this creator&apos;s offers.
              </p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="border-b-2 border-ink px-4 py-3 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-black text-lg leading-tight inline-flex items-center gap-2">
              {meta.icon} {meta.heading}
            </h3>
            <p className="text-[11px] text-ink/70 mt-0.5 leading-snug">
              {hasCoins
                ? `Earn ${coinsEarned} ${coinName}${itemTitle ? ` on ${itemTitle}` : ""} + faster checkout on future orders.`
                : meta.sub}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="purchase-auth-gate-close"
            className="shrink-0 border-2 border-ink bg-white p-1 shadow-brutalSm hover:bg-coral hover:text-white"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        </div>
        {/* Auth tabs */}
        <div className="p-4">
          {loyalty === null ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm font-bold text-ink/70">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <AuthTabs
              defaultTab="phone"
              initialMode="login"
              compact
              onSuccess={(data) => onAuthed(data?.user || null)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
    )
  );
}
